import { AddToCartProduct } from 'service/adobe-analytics/types';
import {
  Arrangement,
  Product,
  SelectedProduct,
} from 'redux/PDP/arrangement/types/arrangement.types';
import { ArrangementPreview } from 'api/arrangements/types';
import { AvailabilityOnDate } from 'api/availability-on-date/types/availability-on-date.interface';
import { CaseItem } from 'redux/case/caseOrder/case-order.types';
import {
  MarketplaceSale,
  Order,
  OrderItem,
  OrderSubItem,
  Sale,
} from 'redux/order-confirmation/types/types';
import { SearchType } from 'enums/search-type';
import { Store } from 'redux/types/store.types';
import { UpsellItem } from 'components/PDP/Upsells/feature/types/upsell-item';
import { UpsellOption } from 'components/PDP/Upsells/feature/types/upsell-option';
import {
  UserAccountInformation,
  UserSession,
} from 'providers/Session/feature/user.types';
import { buildTraits, buildUserTraits } from 'service/segment/utils/traits';
import { getByType } from 'utils/getProductPostfixByType';
import { getCookieValue } from 'service/segment/utils/cookie-value';
import { isUS } from 'utils/country';
import { sha256 } from 'crypto-hash';

const { href: url, pathname } = window.location;
const { title, referrer } = window.document;

export class SegmentAnalytics {
  protected analytics: SegmentAnalytics.AnalyticsJS;

  private static instance: SegmentAnalytics;

  constructor() {
    this.analytics = window.analytics;
  }

  public getFriendlyUrl = (): string =>
    window.location.pathname.replace(/.*\/([^/]+)\/*$/, '$1');

  public gclid = (): string => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get('gclid') || '';
  };

  public fbclid = (): string => {
    const cookieValue = getCookieValue('_fbc');
    return cookieValue || '';
  };

  public fbp = (): string => {
    const cookieValue = getCookieValue('_fbp');
    return cookieValue || '';
  };

  public segmentAjsId = (): string => {
    const cookieValue = getCookieValue('ajs_anonymous_id');
    return cookieValue || '';
  };

  public static getInstance(): SegmentAnalytics {
    if (!SegmentAnalytics.instance) {
      SegmentAnalytics.instance = new SegmentAnalytics();
    }
    return SegmentAnalytics.instance;
  }

  public identified = async (
    user: UserSession,
    accountInformation?: UserAccountInformation,
  ): Promise<void> => {
    const hashedEmail = await sha256(user.userEmail.toLowerCase());

    analytics.identify(`${hashedEmail}`, {
      name: user.userName,
      email: user.userEmail.toLowerCase(),
      cartCount: user.itemsInCart,
      accountInfo: accountInformation?.accountInfo || [],
      orderHistory: accountInformation?.accountOrderHistory || {},
      rewards: accountInformation?.rewards || [],
    });
  };

  public identify = async (email: string, name?: string): Promise<void> => {
    const hashedEmail = await sha256(email.toLowerCase());
    analytics.identify(`${hashedEmail}`, {
      name,
      email: email.toLowerCase(),
    });
  };

  public pageViewed = (
    name: string,
    productName?: string,
    catalogNumber?: number,
    variant?: Product[],
    brand?: string[],
    storeNumber?: number,
    storeAddress?: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.page(
      name,
      {
        name,
        title,
        url,
        pathname,
        productName,
        catalogNumber,
        variant,
        referrer,
        brand,
        storeNumber,
        storeAddress,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public trackSearched = (
    searchValue: string,
    searchCount: number,
    searchSKUResults: string[],
    user?: UserSession,
    accountInformation?: UserAccountInformation,
    searchType?: SearchType,
  ): void => {
    analytics.track(
      'Products Searched',
      {
        searchValue,
        searchCount,
        searchSKUResults,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
        searchType,
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public marketingOptIn = (
    signupValue: string,
    optInProperty: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Marketing Opt-In',
      {
        signupValue,
        optInProperty,
        url,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: {
            email:
              optInProperty === 'Email' ? signupValue : user?.userEmail || '',
            phone:
              optInProperty === 'SMS'
                ? signupValue
                : accountInformation?.accountInfo[0].cellPhone ||
                  accountInformation?.accountInfo[0].workPhone ||
                  accountInformation?.accountInfo[0].homePhone ||
                  '',
            birthday: accountInformation?.accountInfo[0].birthday || '',
            firstName: (user && user.userName.split(' ')[0]) || '',
            lastName: (user && user.userName.split(' ')[1]) || '',
            address: {
              city: accountInformation?.accountInfo[0].city || '',
              state: accountInformation?.accountInfo[0].stateAbbreviation || '',
            },
          },
        },
      },
    );
  };

  public menuItemClicked = (
    menuItem: string,
    link?: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Menu Item Clicked',
      {
        menuItem,
        referrer,
        link,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public bannerClicked = (
    link: string,
    name: string,
    bannerPlacement: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Banner Clicked',
      {
        referrer,
        link,
        name,
        bannerPlacement,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public giftFinderClicked = (
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Gift Finder Clicked',
      {
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: {
            email: user?.userEmail,
            phone:
              accountInformation?.accountInfo[0].cellPhone ||
              accountInformation?.accountInfo[0].workPhone ||
              accountInformation?.accountInfo[0].homePhone,
            birthday: accountInformation?.accountInfo[0].birthday,
            firstName: (user && user.userName.split(' ')[0]) || '',
            lastName: (user && user.userName.split(' ')[1]) || '',
            address: {
              city: accountInformation?.accountInfo[0].city,
              state: accountInformation?.accountInfo[0].stateAbbreviation,
            },
          },
        },
      },
    );
  };

  public giftFinderDropDownUsed = (
    occasion: string[],
    price: string[],
    fruit: string[],
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Gift Finder Drop-Down Used',
      {
        occasion,
        price,
        fruit,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public occasionSelected = (
    name: string,
    link: string,
    sequence: number,
    location: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Occasion Selected',
      {
        name,
        link,
        sequence,
        location,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public productClicked = (
    name: string,
    link: string,
    sequence: number,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Product Clicked',
      {
        name,
        link,
        sequence,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public productViewed = (
    arrangement: Arrangement,
    category: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Product Viewed',
      {
        product_id: arrangement.id,
        sku: arrangement.catalogNumber,
        category,
        name: arrangement.name,
        brand: arrangement.brands[0],
        variant: arrangement.products[0].name,
        price: arrangement.products[0].price,
        quantity: 1,
        currency: isUS ? 'usd' : 'cad',
        value: arrangement.products[0].price,
        url: this.getFriendlyUrl(),
        image_url: arrangement.products[0].carouselImages[0],
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public productListViewed = (
    arrangements: ArrangementPreview[],
    category: string,
    path: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Product List Viewed',
      {
        category,
        products: arrangements.map((a, i) => ({
          product_id: a.id,
          sku: a.number,
          name: a.name,
          price: a.minPrice,
          position: i + 1,
          category,
          url: path,
          image_url: a.image,
        })),
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public productVariantClicked = (
    product: Product,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Product Variant Clicked',
      {
        referrer,
        name: product.name,
        price: product.price,
        arrangementSizeId: product.arrangementSizeId,
        friendlyUrl: this.getFriendlyUrl(),
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public productQuantityChanged = (
    product: Product,
    quantity: number,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Product Quantity Changed',
      {
        referrer,
        name: product.name,
        price: product.price,
        arrangementSizeId: product.arrangementSizeId,
        quantity,
        friendlyUrl: this.getFriendlyUrl(),
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public productReviewsExpanded = (
    arrangement: Arrangement,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Product Reviews Expanded',
      {
        referrer,
        name: arrangement.name,
        catalogNumber: arrangement.catalogNumber,
        friendlyUrl: this.getFriendlyUrl(),
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public fulfillmentStoreExpanded = (
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Fulfillment Delivery Change Store Expanded',
      {
        referrer,
        friendlyUrl: this.getFriendlyUrl(),
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public fulfillmentStoreClicked = (
    selectedStore: Store,
    sequence: number,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Product Page Fulfillment Store Selected',
      {
        referrer,
        friendlyUrl: this.getFriendlyUrl(),
        selectedStore,
        sequence: sequence + 1,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public userLoginSuccess = async (
    userEmail: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): Promise<void> => {
    const hashedEmail = await sha256(userEmail.toLowerCase());
    analytics.track(
      'User Login Success',
      {
        referrer,
        url,
        email: userEmail.toLowerCase(),
        user_id: hashedEmail,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildUserTraits(userEmail, user, accountInformation),
        },
      },
    );
  };

  public userForgotPassword = async (
    userEmail: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): Promise<void> => {
    const hashedEmail = await sha256(userEmail.toLowerCase());
    analytics.track(
      'User Forgot Password Request',
      {
        referrer,
        url,
        email: userEmail.toLowerCase(),
        user_id: hashedEmail,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildUserTraits(userEmail, user, accountInformation),
        },
      },
    );
  };

  public storeLockResetClicked = (
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Store Lock Reset Clicked',
      {
        referrer,
        url,
        friendlyUrl: this.getFriendlyUrl(),
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public showMoreAddonsClicked = (
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Show More Addons Clicked',
      {
        referrer,
        url,
        friendlyUrl: this.getFriendlyUrl(),
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public addOnSelected = (
    upsell: UpsellOption,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Add-On Selected',
      {
        referrer,
        url,
        friendlyUrl: this.getFriendlyUrl(),
        upsell,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public printibleSelected = (
    location: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Printible Selected',
      {
        referrer,
        url,
        friendlyUrl: this.getFriendlyUrl(),
        location,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public complimentaryMessageSelected = (
    location: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Complimentary Message Selected',
      {
        referrer,
        url,
        friendlyUrl: this.getFriendlyUrl(),
        location,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public areaNotServicedEvent = (
    zipCode: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Area Not Serviced Event',
      {
        referrer,
        url,
        friendlyUrl: this.getFriendlyUrl(),
        zipCode,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public zipCodeEnteredEvent = (
    zipCode: string,
    products?: Product[],
    byobProducts?: SelectedProduct[],
    isByob?: boolean,
  ): void => {
    analytics.track('Zip Code Entered Event', {
      referrer,
      url,
      friendlyUrl: this.getFriendlyUrl(),
      zipCode,
      gclid: this.gclid(),
      fbclid: this.fbclid(),
      fbp: this.fbp(),
      products: byobProducts && byobProducts.length ? byobProducts : products,
      isByob,
    });
  };

  public deliveryTypeSelected = (
    location: string,
    date?: Date | null,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Delivery Type Selected',
      {
        referrer,
        url,
        friendlyUrl: this.getFriendlyUrl(),
        deliveryDateSelected: date,
        location,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public trackFulfillmentMethod = (
    shippingZipCode?: string,
    selectedDeliveryDate?: Date | null,
    availableDeliveryMethods?: AvailabilityOnDate,
    hasPickupAvailable?: boolean,
    hasDeliveryAvailable?: boolean,
    shippingOnly?: boolean,
    previousStepComplete?: boolean,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
    isByob?: boolean,
  ): void => {
    analytics.track(
      'Checkout Step Viewed',
      {
        referrer,
        url,
        shippingZipCode,
        selectedDeliveryDate,
        availableDeliveryMethods,
        hasPickupAvailable,
        hasDeliveryAvailable,
        isByob,
        shippingOnly,
        previousStepComplete,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public orderTrackSuccess = (
    orderNumber: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Order Track Success',
      {
        referrer,
        orderNumber,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public orderTrackFailed = (
    orderNumber: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Order Track Failed',
      {
        referrer,
        orderNumber,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public contactUsFormSubmitted = (
    caseData: CaseItem[],
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    const source =
      window.location.search === '?hideNavigation=1' ? 'app' : 'web';
    analytics.track(
      'Contact Us Form Submitted',
      {
        caseData,
        source,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public productAdded = ({
    product,
    addonItems,
    user,
    accountInformation,
  }: {
    product: AddToCartProduct;
    addonItems: UpsellItem[];
    user?: UserSession;
    accountInformation?: UserAccountInformation;
  }): void => {
    const addons = addonItems.map((addon) => ({
      productInfo: {
        id: `${addon.option.productId}${getByType(addon.productType)}`,
        units: Number(addon.quantity),
        price: addon.option.price,
      },
    }));

    analytics.track(
      'Product Added',
      {
        products: [{ productInfo: product }, ...addons],
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public orderCompleted = (
    saleData: Sale,
    order: Order[],
    products: OrderItem[],
    addOns: OrderSubItem[],
    expressFulfillment?: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    order.forEach((orderData) => {
      let discountType = '';
      if (orderData.couponCode) {
        discountType = 'coupon';
      }
      let fulfillment = '';
      const productsInfo: {
        productInfo: {
          id: string;
          price: string;
          units: string;
          discount: string;
          discount_type: string;
          isByob: boolean;
        };
      }[] = [];

      products
        ?.filter((product) => product.orderID === orderData.orderID)
        ?.map((product) => {
          const discount = product.regularPrice
            ? (product.regularPrice - product.price + product.discount).toFixed(
                2,
              )
            : product.discount;
          const price = product.regularPrice
            ? product.regularPrice
            : product.price;
          const discountTypePrice = product.regularPrice
            ? 'slash pricing'
            : discountType;
          const discountTypeValue =
            !!discountType && product.regularPrice
              ? `coupon | slash pricing`
              : discountTypePrice;
          const productsInfoValue = {
            id:
              product.type === 2
                ? `${product.productID}_package`
                : `${product.productID}_arr`,
            price: `${price}`,
            units: `${product.quantity}`,
            discount: `${discount}`,
            discount_type: discountTypeValue,
            isByob: product.isByob,
          };
          const subObject = { productInfo: productsInfoValue };
          productsInfo.push(subObject);
          return true;
        });

      const addOnInfo = addOns
        .filter(
          (a) =>
            a.orderID === orderData.orderID && a.type === 4 && a.itemType !== 2,
        )
        .map((addOn) => ({
          productInfo: {
            id: `${addOn.productID}_addon`,
            price: `${addOn.price}`,
            units: `${addOn.quantity}`,
            discount: `${addOn.discount}`,
            discount_type: `${discountType}`,
            isByob: addOn.isByob,
          },
        }));

      const upgradeInfo = addOns
        .filter(
          (a) =>
            a.orderID === orderData.orderID && a.type === 3 && a.itemType !== 2,
        )
        .map((addOn) => ({
          productInfo: {
            id: `${addOn.productID}_upgrade`,
            price: `${addOn.price}`,
            units: `${addOn.quantity}`,
            discount: `${addOn.discount}`,
            discount_type: `${discountType}`,
            isByob: addOn.isByob,
          },
        }));

      if (order[0].orderType === 1) fulfillment = 'hand delivery';
      if (order[0].orderType === 2) fulfillment = 'pickup';
      if (order[0].orderType === 3) fulfillment = 'shipment';

      const deliveryMethod = expressFulfillment || fulfillment;
      const isByob = productsInfo.some((product) => product.productInfo.isByob);

      const orderTotal =
        orderData.orderTotal -
        (orderData.orderTax1 +
          orderData.orderTax2 +
          orderData.orderTax3 +
          orderData.orderTax4 +
          orderData.orderTax5);

      analytics.track(
        'Order Completed',
        {
          products: [...productsInfo, ...addOnInfo, ...upgradeInfo],
          transaction: {
            couponCode: orderData.couponCode,
            deliveryOrShippingAmount: `${orderData.deliveryOrShipmentCharge}`,
            deliveryOrShippingDiscount: `${orderData.deliveryOrShipmentChargeDiscount}`,
            fulfillingStoreNumber: `${orderData.storeNumber}`,
            fulfillmentMethod: fulfillment,
            orderDiscount: `${orderData.orderDiscount}`,
            orderID: `${orderData.number}`,
            orderOccasion: `${orderData.occasion}`,
            paymentMethod: 'Visa',
            recipientZipCode: `${orderData.area}`,
            taxAmount: `${
              orderData.orderTax1 +
              orderData.orderTax2 +
              orderData.orderTax3 +
              orderData.orderTax4 +
              orderData.orderTax5
            }`,
            totalAmount: `${orderTotal}`,
            transactionID: `${orderData.number}`,
            deliveryMethod,
          },
          gclid: this.gclid(),
          fbclid: this.fbclid(),
          fbp: this.fbp(),
          isByob,
        },
        {
          context: {
            traits: {
              email: saleData.email || user?.userEmail,
              phone:
                saleData.cellPhone ||
                saleData.homePhone ||
                saleData.workPhone ||
                '',
              birthday: accountInformation?.accountInfo[0].birthday || '',
              firstName: saleData.customerName.split(' ')[0] || '',
              lastName: saleData.customerName.split(' ')[1] || '',
              address: {
                city: saleData.city || '',
                state: saleData.state || '',
                postalCode: saleData.area || '',
              },
            },
          },
        },
      );
    });
  };

  public marketPlaceOrderCompleted = (saleData: MarketplaceSale): void => {
    saleData.marketplaceOrders.forEach((order) => {
      const mpProducts = order.marketplaceOrderBrandItems.flatMap(
        (brandItem) => brandItem.products,
      );

      analytics.track(
        'Order Completed',
        {
          orderId: order.orderID,
          number: order.number,
          products: [...mpProducts],
          transaction: {
            deliveryOrShippingAmount: order.deliveryOrShipmentCharge,
            deliveryOrShippingDiscount: order.deliveryOrShipmentChargeDiscount,
            orderDiscount: order.orderDiscount,
            orderID: order.number,
            taxAmount: order.orderTax,
            totalAmount: order.orderTotal - order.orderTax,
            transactionID: order.number,
          },
          gclid: this.gclid(),
          fbclid: this.fbclid(),
          fbp: this.fbp(),
        },
        {
          context: {
            traits: {
              email: saleData.email,
              phone:
                saleData.cellPhone || saleData.homePhone || saleData.workPhone,
              firstName: saleData.customerName.split(' ')[0] || '',
              lastName: saleData.customerName.split(' ')[1] || '',
              address: {
                city: saleData.city,
                state: saleData.state,
                postalCode: saleData.area,
              },
            },
          },
        },
      );
    });
  };

  public changedRelationshipStatus = (
    type: string,
    bannerPosition: number,
  ): void => {
    analytics.track(`Valentines day slider relations ship changed`, {
      referrer,
      type,
      gclid: this.gclid(),
      fbclid: this.fbclid(),
      fbp: this.fbp(),
      bannerPosition,
    });
  };

  public viewAllRelationshipEvent = (
    viewAllUrl: string,
    bannerPosition: number,
  ): void => {
    analytics.track(`Valentines day view all clicked`, {
      referrer,
      viewAllUrl,
      gclid: this.gclid(),
      fbclid: this.fbclid(),
      fbp: this.fbp(),
      bannerPosition,
    });
  };

  public ScrollRelationshipEvent = (bannerPosition: number): void => {
    analytics.track(`Valentines day slider scrolled event`, {
      referrer,
      bannerPosition,
      gclid: this.gclid(),
      fbclid: this.fbclid(),
      fbp: this.fbp(),
    });
  };

  public TrackProductRelationshipEvent = (
    bannerPosition: number,
    item: {
      sku: number;
      price: number;
      positionProduct: number;
    },
  ): void => {
    analytics.track(`Valentines day slider products`, {
      referrer,
      bannerPosition,
      gclid: this.gclid(),
      fbclid: this.fbclid(),
      fbp: this.fbp(),
      item,
    });
  };

  public heroCtaClicked = (
    ctaCopy: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Goody Landing: Hero CTA Clicked',
      {
        ctaCopy,
        referrer,
        url,
        friendlyUrl: this.getFriendlyUrl(),
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public shoppingModuleClicked = (
    skuModuleName: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Goody Landing: Shopping Module Clicked',
      {
        skuModuleName,
        referrer,
        url,
        friendlyUrl: this.getFriendlyUrl(),
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public shoppingModulePopupClicked = (
    ctaCopy: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Goody Landing: Shopping Module Popup Clicked',
      {
        ctaCopy,
        referrer,
        url,
        friendlyUrl: this.getFriendlyUrl(),
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public sliderProductClicked = (
    bannerPosition: number,
    item: {
      sku: number;
      price: number;
      positionProduct: number;
      name?: string;
    },
  ): void => {
    analytics.track(`Goody landing page slider product clicked`, {
      referrer,
      bannerPosition,
      gclid: this.gclid(),
      fbclid: this.fbclid(),
      fbp: this.fbp(),
      item,
    });
  };

  public faqEngaged = (
    action: 'open' | 'close',
    sequence: number,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Goody Landing: FAQ Engaged',
      {
        action,
        sequence,
        referrer,
        url,
        friendlyUrl: this.getFriendlyUrl(),
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public tipAtCheckoutClicked = (
    tipAmount: string,
    tipType: 'preset' | 'custom' | 'none',
    isPercentage: boolean,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Tipping At Checkout Clicked',
      {
        tipAmount,
        tipType,
        isPercentage,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public cateringEventDetailsEntered = (
    eventDate: string,
    eventTime: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Catering Event Details Entered',
      {
        eventDate,
        eventTime,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public cateringWidgetBannerClicked = (
    bannerName: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Catering Widget Banner Clicked',
      {
        bannerName,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public cateringLoadMoreClicked = (
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Catering Load More Button Clicked',
      {
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public cateringViewProductDetails = (
    skuName: string,
    skuNumber: string,
    price: number,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Catering View Product Details',
      {
        skuName,
        skuNumber,
        price,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public cateringAddToCartClicked = (
    clickLocation: 'PLP' | 'Product Modal',
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Catering Add to Cart Button Clicked',
      {
        clickLocation,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public cateringSizeOrStyleSelected = (
    menuCopy: string,
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Catering Size or Style Selected',
      {
        menuCopy,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public cateringMiniCartCheckoutClicked = (
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Catering Mini Cart Checkout Button Clicked',
      {
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public cateringMiniCartUpdate = (
    action: 'Add' | 'Delete',
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Catering Mini Cart Update',
      {
        action,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public cateringMarketingOptIn = (
    signUpType: 'Rewards Member' | 'Marketing',
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Catering Marketing Opt-In',
      {
        signUpType,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public cateringAccountCreated = (
    location: 'Checkout' | 'Order confirmation/thank you',
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Catering Account Created',
      {
        location,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public mobileDownloadBannerAppeared = (
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Mobile Download Banner Appeared',
      {
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public mobileDownloadBannerClosed = (
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Mobile Download Banner Closed',
      {
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public mobileDownloadBannerPressed = (
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Mobile Download Banner Pressed',
      {
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };

  public productRefinement = (
    filterData: {
      filterName: string;
      filterValue: string;
      filterType: string;
    },
    user?: UserSession,
    accountInformation?: UserAccountInformation,
  ): void => {
    analytics.track(
      'Product Refinement',
      {
        filterName: filterData.filterName,
        filterValue: filterData.filterValue,
        filterType: filterData.filterType,
        gclid: this.gclid(),
        fbclid: this.fbclid(),
        fbp: this.fbp(),
      },
      {
        context: {
          traits: buildTraits(user, accountInformation),
        },
      },
    );
  };
}

export const segment: SegmentAnalytics = SegmentAnalytics.getInstance();
